/**
 * Phase 8 C-14 §22 — Library source detail drawer.
 *
 * 420 px right drawer (Mobbin frame 82). Opens when a SourceCard is clicked.
 * Shows: name, description, connection / candidate / member counts, last synced.
 * Back chevron + Esc close without a route change (handled by <Drawer>).
 */

import { ChevronLeft, Users, UserCheck, Clock } from 'lucide-react';
import { Drawer } from '@shared/components/ui/Drawer';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { StatusChip } from '@shared/components/ui/StatusChip';
import type { LibraryNetwork, SyncStatus } from './types';

// ── helpers ──────────────────────────────────────────────────────────────────

function syncLabel(s: SyncStatus): string {
  if (s === 'synced')  return 'Active';
  if (s === 'syncing') return 'Syncing';
  return 'Disconnected';
}

function syncDotColor(s: SyncStatus): string {
  if (s === 'synced')  return '#10B981';
  if (s === 'syncing') return '#F59E0B';
  return '#EF4444';
}

function fmtDate(iso?: string): string {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── stat row ─────────────────────────────────────────────────────────────────

function StatRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] vscode-bg-tertiary vscode-text-tertiary">
        {icon}
      </span>
      <span className="flex-1 text-[13px] vscode-text-secondary">{label}</span>
      <span className="text-[13px] font-medium vscode-text-primary tabular-nums">{value}</span>
    </div>
  );
}

// ── panel ────────────────────────────────────────────────────────────────────

interface LibrarySourceDetailPanelProps {
  open: boolean;
  network: LibraryNetwork | null;
  onClose: () => void;
}

export function LibrarySourceDetailPanel({
  open,
  network,
  onClose,
}: LibrarySourceDetailPanelProps) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      panelMaxWidthPx={420}
      ariaLabel="Source detail"
      showCloseButton={false}
    >
      {network ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* ── header ── */}
          <header className="flex shrink-0 items-center gap-2 border-b px-4 py-3 vscode-border">
            <button
              type="button"
              onClick={onClose}
              className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
              aria-label="Back"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden />
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-semibold vscode-text-primary">
                {network.name}
              </p>
            </div>
            <StatusChip
              label={syncLabel(network.syncStatus)}
              dotColor={syncDotColor(network.syncStatus)}
            />
          </header>

          {/* ── scrollable body ── */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">

            {/* Avatar + name block */}
            <div className="flex items-center gap-3">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] text-[15px] font-semibold"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--ggj-accent,#10B981) 15%, transparent)',
                  color: 'var(--ggj-accent,#10B981)',
                  border: '1px solid color-mix(in srgb, var(--ggj-accent,#10B981) 30%, transparent)',
                }}
                aria-hidden
              >
                {network.iconInitials}
              </div>
              <div className="min-w-0">
                <p className="text-[14px] font-semibold vscode-text-primary truncate">
                  {network.name}
                </p>
                <p className="text-[12px] vscode-text-tertiary">
                  Uploaded by {network.uploadedBy}
                </p>
              </div>
            </div>

            {/* Description */}
            {network.description ? (
              <p className="text-[13px] vscode-text-secondary leading-relaxed">
                {network.description}
              </p>
            ) : null}

            {/* Divider */}
            <div className="border-t vscode-border" />

            {/* Counts + last synced */}
            <div className="divide-y vscode-border rounded-[10px] border vscode-border overflow-hidden">
              <StatRow
                icon={<Users className="h-3.5 w-3.5" aria-hidden />}
                label="Connections"
                value={network.connectionCount.toLocaleString()}
              />
              <StatRow
                icon={<UserCheck className="h-3.5 w-3.5" aria-hidden />}
                label="Candidates sourced"
                value={network.candidateCount.toLocaleString()}
              />
              <StatRow
                icon={<Users className="h-3.5 w-3.5" aria-hidden />}
                label="Members with access"
                value={network.memberCount.toLocaleString()}
              />
              <StatRow
                icon={<Clock className="h-3.5 w-3.5" aria-hidden />}
                label="Last synced"
                value={fmtDate(network.lastSynced)}
              />
            </div>

            {/* Uploaded by row */}
            <div className="flex items-center gap-2">
              <TableAvatar name={network.uploadedBy} size={20} />
              <span className="text-[12px] vscode-text-secondary">
                Uploaded by <span className="font-medium vscode-text-primary">{network.uploadedBy}</span>
              </span>
            </div>

          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
